/**
 * useCreateCharge - Hook orquestrador para criação de cobranças
 * 
 * Fluxo em 2 etapas:
 * 1. Criar cobrança via RPC atômica (financeiro_cobrancas + financeiro_cobranca_itens + parcelas)
 * 2. Enviar ao provider via Edge Function financial-charge
 * 
 * @author Módulo Financeiro
 * @version 2.5.1
 * RELEASE: 2026-09-03 v2.5.1 - Bloqueios do Workflow usam aviso modal global.
 * RELEASE: 2026-03-15 v1.0.0 - Criação do hook orquestrador
 * RELEASE: 2026-03-15 v2.0.0 - Refatoração para RPC atômica create_financeiro_cobranca
 * RELEASE: 2026-06-17 v2.1.0 - Unifica chamada ao provider via dispatchCobrancaToProvider
 *                              (helper v2.1.0). Backend (financial-charge v1.8.0/v1.9.0)
 *                              carrega valor/vencimento/descrição/parcelas direto do
 *                              banco — elimina divergência local↔Asaas em avulsa e
 *                              parcelada.
 * RELEASE: 2026-06-17 v2.2.0 - Suporte a juros/multa/expiração em cobrança avulsa
 *                              e parcelada (paridade com assinatura). Repassa
 *                              p_juros e p_dias_expiracao p/ a RPC v1.4.0.
 */

import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';
import { dispatchCobrancaToProvider } from '@/lib/financial/dispatchCobranca';
import { persistDescricaoTemplate, hasDescricaoVariables } from '@/hooks/financial/useFinanceiroDescricaoTemplate';
import type { TipoCobranca } from '@/hooks/useFinanceiroCobrancas';

const logger = createLogger('CreateCharge');

/** Interface de um item da cobrança vinculado ao catálogo */
export interface ChargeItemInput {
  product_id: string;
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  desconto: number;
  // Campos opcionais usados apenas em assinaturas (CreateSubscriptionModal):
  // preservam o TIPO do desconto (percent|fixed) e o percentual original
  // para que a tela de edição reabra o toggle %/R$ fielmente.
  // Persistidos em financeiro_cobranca_itens.metadata pela RPC.
  desconto_tipo?: 'percent' | 'fixed';
  desconto_percent?: number;
}

/** Interface completa para criação de cobrança */
export interface CreateChargeInput {
  /** ID do cliente CRM */
  customer_id: string;
  /** ID do deal vinculado (opcional) */
  deal_id?: string | null;
  /** Tipo: única ou parcelada */
  tipo_cobranca: TipoCobranca;
  /** ID do meio de pagamento */
  meio_pagamento_id: string;
  /** ID da conta corrente (com provider) */
  conta_corrente_id: string;
  /** Itens da cobrança (mínimo 1) */
  itens: ChargeItemInput[];
  /** Data de vencimento (YYYY-MM-DD) */
  data_vencimento: string;
  /** Número de parcelas (1 para única, 2-12 para parcelada) */
  numero_parcelas: number;
  /** Observações internas */
  observacoes?: string;
  /** Moeda (padrão BRL) */
  moeda?: string;
  /** Descrição principal da cobrança */
  descricao?: string;
  /**
   * v2.2.0 — Juros e multa por atraso (mesmo schema da assinatura).
   * Estrutura: { enabled, multa_tipo, multa_valor, juros_tipo, juros_valor }.
   * Quando omitido, persistido como {} (sem juros/multa).
   */
  juros?: Record<string, unknown>;
  /**
   * v2.2.0 — Prazo em dias após o vencimento em que a cobrança continua
   * paga (1..60). Mapeia para dueDateLimitDays no Asaas. Padrão: 60.
   */
  dias_expiracao?: number;
  /**
   * v2.3.0 — Veículos do cliente vinculados à cobrança (opcional).
   * Quando informado, a criação usa a RPC wrapper atômica
   * `create_financeiro_cobranca_with_vehicles` (cobrança + vínculos na mesma
   * transação). Sem veículos, o fluxo original permanece intacto.
   */
  vehicle_ids?: string[];
  /**
   * v2.5.0 — Tarefa de origem da cobrança (Retenção/Jurídico/Normal).
   * Gravado em financeiro_cobrancas.task_id pela RPC (validado no backend).
   */
  task_id?: string;
  /**
   * v2.6.0 — Quando true, a cobrança é criada apenas localmente (controle
   * manual) e NÃO é despachada ao provedor. Usado quando a conta corrente
   * escolhida não possui integração de pagamento (`provider_slug` nulo).
   */
  skip_provider_dispatch?: boolean;
  /**
   * v2.7.0 — Liga/desliga a régua de cobrança desta cobrança (seção
   * "Lembretes automáticos" do modal). Quando omitido, o banco mantém o
   * padrão `true`. O worker valida o flag NO MOMENTO DO DISPARO.
   */
  reminders_enabled?: boolean;
}


/**
 * Hook orquestrador para criação de cobranças
 * Utiliza RPC atômica para garantir transação completa no banco
 * e envia ao provider financeiro após sucesso local
 */
export function useCreateCharge() {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  const workflowNotice = useWorkflowGuardNotice();
  const tenantId = currentTenant?.id;

  // Ref para prevenir dupla submissão (race condition antes do React Query atualizar isPending)
  const submittingRef = useRef(false);

  const createCharge = useMutation({
    mutationFn: async (input: CreateChargeInput) => {
      // === GUARD ANTI-DUPLICAÇÃO FRONTEND ===
      if (submittingRef.current) {
        throw new Error('Cobrança já está sendo criada. Aguarde.');
      }
      submittingRef.current = true;

      try {
      if (!tenantId) throw new Error('Tenant não encontrado');

      // Calcular valor total dos itens
      const valorTotal = input.itens.reduce((sum, item) => {
        return sum + (item.quantidade * item.valor_unitario - item.desconto);
      }, 0);

      const descricaoGeral = input.descricao || input.itens.map(i => i.descricao).join(', ');

      logger.info('Criando cobrança via RPC atômica', { 
        tipo: input.tipo_cobranca, 
        itens: input.itens.length, 
        valorTotal 
      });

      // === ETAPA 1: Criar cobrança via RPC atômica ===
      // A RPC cria cobrança + itens + parcelas em transação única
      // Se qualquer etapa falhar, nada é persistido (rollback automático)
      // v2.3.0 — com veículos selecionados usamos a RPC wrapper, que chama a
      // RPC original e grava os vínculos na MESMA transação.
      const hasVehicles = (input.vehicle_ids?.length ?? 0) > 0;

      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        (hasVehicles
          ? 'create_financeiro_cobranca_with_vehicles'
          : 'create_financeiro_cobranca') as any,
        {
          p_customer_id: input.customer_id,
          p_tipo_cobranca: input.tipo_cobranca,
          p_meio_pagamento_id: input.meio_pagamento_id,
          p_conta_corrente_id: input.conta_corrente_id,
          p_valor_total: valorTotal,
          p_data_vencimento: input.data_vencimento,
          p_numero_parcelas: input.numero_parcelas,
          p_descricao: descricaoGeral,
          p_moeda: input.moeda || 'BRL',
          p_observacoes: input.observacoes || null,
          p_deal_id: input.deal_id || null,
          // v2.5.0 — vínculo opcional com a tarefa de origem
          p_task_id: input.task_id || null,
          p_itens: input.itens,
          p_tenant_hint: tenantId,
          // v2.2.0 — juros/multa/expiração (avulsa e parcelada)
          p_juros: input.juros ?? {},
          p_dias_expiracao: input.dias_expiracao ?? 60,
          // v2.7.0 — régua de cobrança (NULL = mantém o padrão do banco).
          // A wrapper de veículos também aceita o parâmetro (mesma assinatura).
          p_reminders_enabled: input.reminders_enabled ?? null,
          ...(hasVehicles ? { p_vehicle_ids: input.vehicle_ids } : {}),
        }
      );


      if (rpcError) {
        logger.error('Erro na RPC create_financeiro_cobranca', { error: rpcError.message });
        throw new Error(rpcError.message);
      }

      // Extrair dados retornados pela RPC
      const cobrancaId = rpcResult?.cobranca_id;
      const tipoPagamento = rpcResult?.tipo_pagamento || 'pix';

      logger.info('Cobrança criada com sucesso via RPC', { cobrancaId });

      // === ETAPA 1.5: Template de descrição (v2.4.0) ===
      // Quando a descrição informada contém variáveis ({{...}}), persistimos o
      // TEMPLATE e deixamos o banco renderizar o snapshot em `descricao` ANTES
      // do envio ao provider — garante que o Asaas receba o texto final.
      if (cobrancaId && hasDescricaoVariables(descricaoGeral)) {
        await persistDescricaoTemplate({
          template: descricaoGeral,
          cobrancaId,
          tenantHint: tenantId,
        });
      }

      // === ETAPA 2: Enviar ao provider via helper unificado ===
      // v2.1.0 (2026-06-17): backend usa banco como fonte de verdade.
      // valor/vencimento/descrição/parcelas vêm de financeiro_cobrancas e
      // financeiro_parcelas a partir do cobranca_id — payload enxuto.
      // v2.6.0 (2026-09-12): conta sem integração (controle manual) pula a
      // ETAPA 2 por completo — a cobrança permanece local em 'pendente'.

      if (input.skip_provider_dispatch) {
        logger.info('Cobrança manual — envio ao provider ignorado', { cobrancaId });
      } else {
        try {
          const ok = await dispatchCobrancaToProvider({
            action: input.tipo_cobranca === 'parcelada' ? 'create_installment' : 'create_single',
            cobranca_id: cobrancaId,
            conta_corrente_id: input.conta_corrente_id,
            crm_customer_id: input.customer_id,
            tipo_pagamento: tipoPagamento,
            observacoes: input.observacoes || null,
            origin: 'manual_charge_create',
          });

          if (!ok) {
            toast.warning('Cobrança criada localmente. Envio ao provider será retentado.');
          } else {
            logger.info('Cobrança enviada ao provider com sucesso');
          }
        } catch (err) {
          logger.warn('Falha na comunicação com provider', { error: String(err) });
          toast.warning('Cobrança salva. Sincronização com provider pendente.');
        }
      }

      // Retornar objeto com id para compatibilidade com o fluxo existente
      return { id: cobrancaId, ...rpcResult };
      } finally {
        // Liberar trava de submissão em qualquer cenário (sucesso ou erro)
        submittingRef.current = false;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      toast.success('Cobrança criada com sucesso!');
    },
    onError: (error: Error) => {
      logger.error('Erro ao criar cobrança', { error });
      // Trava de processo é aviso de negócio, não erro técnico.
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      toast.error(`Erro ao criar cobrança: ${error.message}`);
    },
  });

  return {
    createCharge,
    isCreating: createCharge.isPending,
  };
}
